import React from "react";
import { Cookie } from "lucide-react";
import { siteContact } from "@/config/site";
import LegalPage, { LegalSection, LegalList, LegalContactBox } from "@/components/shared/LegalPage";

const COOKIE_TYPES = [
  {
    title: "Essential Cookies",
    desc: "Necessary for the website to function properly. They enable basic features like page navigation, secure access, and session management. The website cannot function properly without these cookies.",
  },
  {
    title: "Performance Cookies",
    desc: "Help us understand how visitors interact with our website by collecting information anonymously. They help us improve our website's performance and user experience.",
  },
  {
    title: "Functional Cookies",
    desc: "Allow our website to remember choices you make (such as your language preference or region) and provide enhanced, personalized features.",
  },
  {
    title: "Analytics Cookies",
    desc: "We use analytics cookies to track website traffic and understand visitor behavior. This helps us improve our content and services. The information collected is aggregated and anonymous.",
  },
];

const COOKIE_LIST = [
  { name: "session_id",     purpose: "User authentication",                duration: "Session" },
  { name: "cookieConsent",  purpose: "Stores cookie preferences",          duration: "1 year" },
  { name: "_ga",            purpose: "Google Analytics tracking",          duration: "2 years" },
  { name: "_gid",           purpose: "Google Analytics (user distinction)",duration: "24 hours" },
];

const CookiePolicy = () => {
  return (
    <LegalPage
      eyebrow="Privacy"
      eyebrowIcon={Cookie}
      title={<>Cookie <span className="gradient-text">Policy</span></>}
      lastUpdated="January 2026"
    >
      <LegalSection title="1. What Are Cookies?">
        <p>
          Cookies are small text files that are placed on your computer or mobile device when you visit a website.
          They are widely used to make websites work more efficiently, provide information to the owners of the
          site, and enhance user experience. Cookies allow websites to remember your preferences and recognize you
          on subsequent visits.
        </p>
      </LegalSection>

      <LegalSection title="2. How We Use Cookies">
        <p>NameSpace Consultants uses cookies for several purposes:</p>
        <LegalList>
          <li>To enable certain functions of our website</li>
          <li>To provide analytics and understand how visitors use our site</li>
          <li>To store your preferences and settings</li>
          <li>To improve site performance and user experience</li>
          <li>To remember your login information</li>
        </LegalList>
      </LegalSection>

      <LegalSection title="3. Types of Cookies We Use">
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          {COOKIE_TYPES.map((c) => (
            <div
              key={c.title}
              className="rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-1)]/50 p-5"
            >
              <h3 className="font-semibold gradient-text-cool mb-2">{c.title}</h3>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </LegalSection>

      <LegalSection title="4. Cookie List">
        <div className="overflow-x-auto rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-1)]/40">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[color:var(--border-default)] text-left">
                <th className="py-3 px-4 font-semibold text-[var(--brand-primary-light)]">Cookie Name</th>
                <th className="py-3 px-4 font-semibold text-[var(--brand-primary-light)]">Purpose</th>
                <th className="py-3 px-4 font-semibold text-[var(--brand-primary-light)]">Duration</th>
              </tr>
            </thead>
            <tbody>
              {COOKIE_LIST.map((c) => (
                <tr
                  key={c.name}
                  className="border-b border-[color:var(--border-subtle)] last:border-0 text-[var(--text-secondary)]"
                >
                  <td className="py-3 px-4 font-mono text-xs">{c.name}</td>
                  <td className="py-3 px-4">{c.purpose}</td>
                  <td className="py-3 px-4">{c.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection title="5. Third-Party Cookies">
        <p>
          Some cookies are placed by third-party services that appear on our pages. We use trusted third-party
          services like Google Analytics to help us understand how visitors use our website. These third parties
          may also use cookies, and their use is governed by their own privacy policies.
        </p>
      </LegalSection>

      <LegalSection title="6. Managing Your Cookie Preferences">
        <p>You have the right to decide whether to accept or reject cookies. You can manage your cookie preferences in several ways:</p>
        <LegalList>
          <li><strong className="text-[var(--text-primary)]">Cookie Banner:</strong> Use our cookie consent banner when you first visit our site</li>
          <li><strong className="text-[var(--text-primary)]">Browser Settings:</strong> Most browsers allow you to control cookies through their settings</li>
          <li><strong className="text-[var(--text-primary)]">Opt-Out Links:</strong> You can opt out of certain analytics cookies through third-party tools</li>
        </LegalList>
        <p>Please note that disabling certain cookies may affect the functionality of our website.</p>
      </LegalSection>

      <LegalSection title="7. Browser Cookie Settings">
        <p>Here's how to manage cookies in popular browsers:</p>
        <LegalList>
          <li><strong className="text-[var(--text-primary)]">Chrome:</strong> Settings → Privacy and Security → Cookies</li>
          <li><strong className="text-[var(--text-primary)]">Firefox:</strong> Options → Privacy & Security → Cookies</li>
          <li><strong className="text-[var(--text-primary)]">Safari:</strong> Preferences → Privacy → Cookies</li>
          <li><strong className="text-[var(--text-primary)]">Edge:</strong> Settings → Privacy & Security → Cookies</li>
        </LegalList>
      </LegalSection>

      <LegalSection title="8. Changes to This Policy">
        <p>
          We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our
          data practices. Any changes will be posted on this page with an updated revision date. We encourage you
          to check this page periodically for updates.
        </p>
      </LegalSection>

      <LegalSection title="9. Contact Us">
        <p>If you have any questions about our use of cookies, please contact us:</p>
        <LegalContactBox
          name="NameSpace Consultants"
          address={siteContact.address.full}
          email={siteContact.email}
          phone={siteContact.phone}
        />
      </LegalSection>
    </LegalPage>
  );
};

export default CookiePolicy;
